export type SiteFlipPhase = "miniature" | "expanding" | "fullscreen";

export interface SiteFlipFrame {
  phase: SiteFlipPhase;
  flip: number;
}

export interface SiteFlipViewport {
  width: number;
  height: number;
}

export interface SiteFlipRect {
  left: number;
  top: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

export interface SiteFlipGeometry {
  first: SiteFlipRect;
  last: SiteFlipRect;
  inverse: {
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
  };
}

export type SiteFlipRemeasureResult =
  | { status: "updated"; geometry: SiteFlipGeometry }
  | { status: "invalidated" }
  | { status: "failed"; reason: "unmeasurable" };

export interface LiveSiteFlipPort<TNode = HTMLElement> {
  readonly node: TNode;
  readonly adopted: boolean;
  adopt(host: HTMLElement): SiteFlipGeometry;
  apply(frame: SiteFlipFrame): void;
  remeasure(): SiteFlipRemeasureResult;
  release(): void;
}

type ViewportReader = () => SiteFlipViewport;

const FLIP_PROPERTIES = [
  "--uses-site-viewport-width",
  "--uses-site-viewport-height",
  "--uses-site-flip-x",
  "--uses-site-flip-y",
  "--uses-site-flip-scale-x",
  "--uses-site-flip-scale-y",
  "--uses-site-flip-progress",
] as const;

interface Adoption {
  readonly host: HTMLElement;
  readonly portal: HTMLElement;
  readonly parent: Node;
  readonly nextSibling: ChildNode | null;
  readonly placeholder: HTMLElement;
  readonly geometry: SiteFlipGeometry;
  readonly originalStyles: ReadonlyMap<string, string>;
  readonly originalAdopted: string | undefined;
  readonly originalPhase: string | undefined;
  readonly originalHostState: string | undefined;
  readonly addedClass: boolean;
  frame: SiteFlipFrame;
}

interface CanonicalPassage {
  readonly host: HTMLElement;
  readonly geometry: SiteFlipGeometry;
  readonly source: SiteFlipRect;
}

const clamp = (value: number): number => Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
const finite = (value: number): number => Number.isFinite(value) ? value : 0;
const cssNumber = (value: number): string => String(Number(finite(value).toFixed(6)));

const copyRect = (rect: DOMRect): SiteFlipRect => {
  const measurable = [rect.left, rect.top, rect.width, rect.height, rect.right, rect.bottom]
    .every(Number.isFinite);
  return {
    left: finite(rect.left),
    top: finite(rect.top),
    width: measurable ? Math.max(0, rect.width) : 0,
    height: measurable ? Math.max(0, rect.height) : 0,
    right: finite(rect.right),
    bottom: finite(rect.bottom),
  };
};

const viewportTargetRect = (
  target: SiteFlipRect,
  host: SiteFlipRect,
  viewport: SiteFlipViewport,
): SiteFlipRect => {
  if (host.width <= 0 || host.height <= 0) {
    return { left: 0, top: 0, width: 0, height: 0, right: 0, bottom: 0 };
  }
  const centerX = ((target.left - host.left) + target.width / 2) / host.width;
  const centerY = ((target.top - host.top) + target.height / 2) / host.height;
  const left = centerX * viewport.width - target.width / 2;
  const top = centerY * viewport.height - target.height / 2;
  return {
    left,
    top,
    width: target.width,
    height: target.height,
    right: left + target.width,
    bottom: top + target.height,
  };
};

const defaultViewport = (): SiteFlipViewport => ({
  width: Math.max(1, document.documentElement.clientWidth || window.innerWidth),
  height: Math.max(1, window.innerHeight || document.documentElement.clientHeight),
});

export class DomLiveSiteFlipPort implements LiveSiteFlipPort<HTMLElement> {
  private adoption: Adoption | null = null;
  private canonical: CanonicalPassage | null = null;

  constructor(
    readonly node: HTMLElement,
    private readonly readViewport: ViewportReader = defaultViewport,
  ) {}

  get adopted(): boolean {
    return this.adoption !== null;
  }

  adopt(host: HTMLElement): SiteFlipGeometry {
    if (this.adoption?.host === host) return this.adoption.geometry;
    if (this.adoption) this.release();
    return this.adoptTransaction(host, false);
  }

  private adoptTransaction(host: HTMLElement, forceGeometryMeasurement: boolean): SiteFlipGeometry {
    if (this.node === host || this.node.contains(host)) {
      throw new Error("Live site FLIP host cannot be the adopted node or its descendant.");
    }
    const parent = this.node.parentNode;
    if (!parent) throw new Error("Live site FLIP requires a connected source node.");

    const reusableCanonical = this.canonical?.host === host ? this.canonical : null;
    const source = reusableCanonical && !forceGeometryMeasurement
      ? reusableCanonical.source
      : copyRect(this.node.getBoundingClientRect());
    if (source.width <= 0 || source.height <= 0) {
      throw new Error("Live site FLIP could not resolve a measurable source rect.");
    }
    const miniatureTarget = host.querySelector<HTMLElement>("[data-site-miniature-target]");
    if (!miniatureTarget) {
      throw new Error("Live site FLIP requires an explicit miniature target.");
    }
    let width: number;
    let height: number;
    if (reusableCanonical && !forceGeometryMeasurement) {
      width = reusableCanonical.geometry.last.width;
      height = reusableCanonical.geometry.last.height;
    } else {
      const viewport = this.readViewport();
      width = finite(viewport.width);
      height = finite(viewport.height);
      if (width <= 0 || height <= 0) {
        throw new Error("Live site FLIP could not resolve a measurable viewport rect.");
      }
    }
    const viewport = { width, height };
    const first = reusableCanonical && !forceGeometryMeasurement
      ? reusableCanonical.geometry.first
      : viewportTargetRect(
          copyRect(miniatureTarget.getBoundingClientRect()),
          copyRect(host.getBoundingClientRect()),
          viewport,
        );
    if (first.width <= 0 || first.height <= 0) {
      throw new Error("Live site FLIP could not resolve a measurable miniature rect.");
    }
    const portalParent = this.node.ownerDocument.body;
    if (!portalParent) throw new Error("Live site FLIP requires a document body viewport portal.");
    const placeholder = this.node.ownerDocument.createElement("div");
    placeholder.className = "uses-site-flip-placeholder";
    placeholder.setAttribute("aria-hidden", "true");
    placeholder.style.setProperty("width", `${cssNumber(source.width)}px`);
    placeholder.style.setProperty("height", `${cssNumber(source.height)}px`);
    placeholder.style.setProperty("inline-size", `${cssNumber(source.width)}px`);
    placeholder.style.setProperty("block-size", `${cssNumber(source.height)}px`);
    const nextSibling = this.node.nextSibling;
    const originalStyles = new Map<string, string>();
    for (const property of FLIP_PROPERTIES) {
      originalStyles.set(property, this.node.style.getPropertyValue(property));
    }
    const originalAdopted = this.node.dataset.siteFlipAdopted;
    const originalPhase = this.node.dataset.siteFlipPhase;
    const originalHostState = host.dataset.siteFlipHostState;
    const addedClass = !this.node.classList.contains("uses-site-flip-live");
    const portal = this.node.ownerDocument.createElement("div");
    portal.className = "uses-site-flip-viewport-portal";
    portal.setAttribute("data-site-flip-viewport-portal", "");

    parent.insertBefore(placeholder, this.node);
    portalParent.appendChild(portal);
    portal.appendChild(this.node);
    if (addedClass) this.node.classList.add("uses-site-flip-live");
    this.node.dataset.siteFlipAdopted = "true";
    host.dataset.siteFlipHostState = "adopted";
    portal.style.setProperty("--uses-site-viewport-width", `${cssNumber(width)}px`);
    portal.style.setProperty("--uses-site-viewport-height", `${cssNumber(height)}px`);
    this.syncPortalSemantics(host, portal);

    const last = reusableCanonical && !forceGeometryMeasurement
      ? reusableCanonical.geometry.last
      : copyRect(portal.getBoundingClientRect());
    if (last.width <= 0 || last.height <= 0) {
      this.rollbackTransaction({
        host, portal, parent, nextSibling, placeholder,
        geometry: reusableCanonical?.geometry ?? {
          first,
          last,
          inverse: { x: 0, y: 0, scaleX: 1, scaleY: 1 },
        },
        originalStyles, originalAdopted, originalPhase, originalHostState, addedClass,
        frame: { phase: "miniature", flip: 0 },
      });
      throw new Error("Live site FLIP could not resolve a measurable fullscreen rect.");
    }
    const geometry: SiteFlipGeometry = reusableCanonical && !forceGeometryMeasurement
      ? reusableCanonical.geometry
      : {
          first,
          last,
          inverse: {
            x: first.left - last.left,
            y: first.top - last.top,
            scaleX: first.width / last.width,
            scaleY: first.height / last.height,
          },
        };
    this.adoption = {
      host, portal,
      parent,
      nextSibling,
      placeholder,
      geometry,
      originalStyles,
      originalAdopted,
      originalPhase,
      originalHostState,
      addedClass,
      frame: { phase: "miniature", flip: 0 },
    };
    this.canonical = { host, geometry, source };
    this.apply(this.adoption.frame);
    return geometry;
  }

  apply(frame: SiteFlipFrame): void {
    const adoption = this.adoption;
    if (!adoption) return;
    const progress = clamp(frame.flip);
    const inverseProgress = 1 - progress;
    const { inverse } = adoption.geometry;
    const x = inverse.x * inverseProgress;
    const y = inverse.y * inverseProgress;
    const scaleX = 1 + (inverse.scaleX - 1) * inverseProgress;
    const scaleY = 1 + (inverse.scaleY - 1) * inverseProgress;
    adoption.portal.style.setProperty("--uses-site-flip-x", `${cssNumber(x)}px`);
    adoption.portal.style.setProperty("--uses-site-flip-y", `${cssNumber(y)}px`);
    adoption.portal.style.setProperty("--uses-site-flip-scale-x", cssNumber(scaleX));
    adoption.portal.style.setProperty("--uses-site-flip-scale-y", cssNumber(scaleY));
    adoption.portal.style.setProperty("--uses-site-flip-progress", cssNumber(progress));
    this.node.dataset.siteFlipPhase = frame.phase;
    this.syncPortalSemantics(adoption.host, adoption.portal);
    adoption.frame = { phase: frame.phase, flip: progress };
  }

  remeasure(): SiteFlipRemeasureResult {
    const adoption = this.adoption;
    if (!adoption) {
      this.canonical = null;
      return { status: "invalidated" };
    }
    const { host, frame } = adoption;
    this.release();
    try {
      const geometry = this.adoptTransaction(host, true);
      this.apply(frame);
      return { status: "updated", geometry };
    } catch {
      this.canonical = null;
      return { status: "failed", reason: "unmeasurable" };
    }
  }

  release(): void {
    const adoption = this.adoption;
    if (!adoption) return;
    this.adoption = null;

    if (adoption.placeholder.parentNode === adoption.parent) {
      adoption.parent.insertBefore(this.node, adoption.placeholder);
    } else {
      const reference = adoption.nextSibling?.parentNode === adoption.parent ? adoption.nextSibling : null;
      adoption.parent.insertBefore(this.node, reference);
    }
    adoption.placeholder.remove();
    adoption.portal.remove();
    if (adoption.addedClass) this.node.classList.remove("uses-site-flip-live");
    if (adoption.originalAdopted === undefined) delete this.node.dataset.siteFlipAdopted;
    else this.node.dataset.siteFlipAdopted = adoption.originalAdopted;
    if (adoption.originalPhase === undefined) delete this.node.dataset.siteFlipPhase;
    else this.node.dataset.siteFlipPhase = adoption.originalPhase;
    if (adoption.originalHostState === undefined) delete adoption.host.dataset.siteFlipHostState;
    else adoption.host.dataset.siteFlipHostState = adoption.originalHostState;
    for (const property of FLIP_PROPERTIES) {
      const value = adoption.originalStyles.get(property) ?? "";
      if (value) this.node.style.setProperty(property, value);
      else this.node.style.removeProperty(property);
    }
  }

  private rollbackTransaction(adoption: Adoption): void {
    if (adoption.placeholder.parentNode === adoption.parent) {
      adoption.parent.insertBefore(this.node, adoption.placeholder);
    } else {
      const reference = adoption.nextSibling?.parentNode === adoption.parent ? adoption.nextSibling : null;
      adoption.parent.insertBefore(this.node, reference);
    }
    adoption.placeholder.remove();
    adoption.portal.remove();
    if (adoption.addedClass) this.node.classList.remove("uses-site-flip-live");
    if (adoption.originalAdopted === undefined) delete this.node.dataset.siteFlipAdopted;
    else this.node.dataset.siteFlipAdopted = adoption.originalAdopted;
    if (adoption.originalPhase === undefined) delete this.node.dataset.siteFlipPhase;
    else this.node.dataset.siteFlipPhase = adoption.originalPhase;
    if (adoption.originalHostState === undefined) delete adoption.host.dataset.siteFlipHostState;
    else adoption.host.dataset.siteFlipHostState = adoption.originalHostState;
    for (const property of FLIP_PROPERTIES) {
      const value = adoption.originalStyles.get(property) ?? "";
      if (value) this.node.style.setProperty(property, value);
      else this.node.style.removeProperty(property);
    }
  }

  private syncPortalSemantics(host: HTMLElement, portal: HTMLElement): void {
    const section = host.closest<HTMLElement>(".marketing-section") ?? host;
    const hidden = section.getAttribute("aria-hidden") === "true" || section.hasAttribute("inert");
    portal.setAttribute("aria-hidden", hidden ? "true" : "false");
    portal.toggleAttribute("inert", hidden);
  }
}
